import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useMemo, useState } from 'react';
import { addNewMenuItem } from '../shared/addNewMenuItem';
import { useConfirmedDelete } from '../shared/useConfirmedDelete';
import { useWriteScopes } from '../shared/useWriteScopes';
import type { RoleFormMode } from './RoleFormPanel';
import {
  type RolesTableV2Row,
  createRolesTableV2Columns,
} from './rolesTableV2Columns';
import { useRolesTableQuery, useRolesV2Api } from './rolesV2Api';

export type OpenedRole = { mode: RoleFormMode; roleId?: string };

export const useRolesTableV2 = () => {
  const [opened, setOpened] = useState<OpenedRole | null>(null);
  const scopes = useWriteScopes('/roles', '/roles/:roleKey');
  const rolesQuery = useRolesTableQuery();
  const { deleteRole } = useRolesV2Api();

  const { confirmDeleteDialog, requestDelete } =
    useConfirmedDelete<RolesTableV2Row>({
      header: 'Role will be deleted',
      message: (item) =>
        `Do you really want to delete role "${item.name}"? Users and API tokens with this role will lose it.`,
      deleteItem: (item) => deleteRole(item.id),
      successMessage: 'Role deleted',
    });

  const columns = useMemo(
    () =>
      createRolesTableV2Columns({
        scopes,
        onOpen: (roleId, mode) => setOpened({ roleId, mode }),
        onDelete: (row) => void requestDelete(row),
      }),
    [requestDelete, scopes],
  );

  const rows = useMemo<RolesTableV2Row[]>(
    () =>
      (rolesQuery.data?.data ?? []).map((role) => ({
        id: role.id,
        name: role.name,
        description: role.description || '-',
        type: role.type ?? '',
        endpointPolicies: (role.endpointPolicies ?? []).join(', '),
        dataPolicies: (role.dataPolicies ?? []).join(', '),
        // Admin and built-in roles are managed by the system.
        readOnly: Boolean(role.isAdmin || role.isSystem),
      })),
    [rolesQuery.data],
  );

  const frontendSimpleFilter = useFrontendLocalTable({ columns, data: rows });

  const menuItems: TableActionsItem[] = [
    ...(scopes.canCreate
      ? [addNewMenuItem('roles-add-new', () => setOpened({ mode: 'add' }))]
      : []),
  ];

  return {
    columns,
    confirmDeleteDialog,
    frontendSimpleFilter,
    menuItems,
    opened,
    rolesQuery,
    setOpened,
  };
};
